import { useMemo, useState } from 'react'
import { Building2, Heart, List, Search, Banknote } from 'lucide-react'
import BSDCard from '../BSDCard'
import AutoGrid from '../AutoGrid'
import DistrictMap from '../DistrictMap'
import { AMBITI_STATS, type AmbitoId } from '../../data/ambitiStats'
import { CDC_WITH_AMBITO, getUniqueServices } from '../../data/cdcAmbiti'
import { PNRR_INTERVENTI } from '../../data/pnrr'
import CdcDialogList from './CdcDialogList'
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, LabelList, Cell } from 'recharts'
import CompareAmbitiBarCard from '../shared/CompareAmbitiBarCard'
import {
  AMBITO_COMPARE_COLORS,
  formatInt,
  formatEuro,
} from '../../utils/dashboardFormatters'

const CDC_PNRR_AXIS_MAX_CHARS = 12
const CDC_PNRR_AXIS_WIDTH = 76

function wrapCdcPnrrLabel(value: string): string[] {
  const clean = value.trim()
  if (!clean) return ['']
  const words = clean.split(/\s+/)
  const lines: string[] = []
  let current = ''
  for (const word of words) {
    const candidate = current ? `${current} ${word}` : word
    if (candidate.length > CDC_PNRR_AXIS_MAX_CHARS && current) {
      lines.push(current)
      current = word
    } else {
      current = candidate
    }
  }
  if (current) lines.push(current)
  return lines
}

function CdcPnrrAxisTick({ y = 0, payload }: { y?: number; payload?: { value?: string | number } }) {
  const lines = wrapCdcPnrrLabel(String(payload?.value ?? ''))
  return (
    <text x={0} y={y} fill="var(--text-2)" fontSize={10} textAnchor="start">
      {lines.map((line, i) => (
        <tspan key={`${line}-${i}`} x={0} dy={i === 0 ? 4 : 11}>{line}</tspan>
      ))}
    </text>
  )
}

function PnrrTooltip({ active, payload, label }: { active?: boolean; payload?: { value?: number }[]; label?: string }) {
  if (!active || !payload?.length) return null
  return (
    <div style={{ background: 'var(--tooltip-bg)', border: '1px solid var(--tooltip-border)', borderRadius: 8, fontSize: 12, color: 'var(--tooltip-text)', padding: '8px 10px' }}>
      <div style={{ color: 'var(--text-0)', fontWeight: 600, marginBottom: 2 }}>{label}</div>
      <div style={{ color: 'var(--text-2)' }}>{formatEuro(payload[0]?.value ?? 0)}</div>
    </div>
  )
}

function ServiziDialogList({ servizi, numeroCdC }: { servizi: { nome: string; count: number }[]; numeroCdC: number }) {
  const [search, setSearch] = useState('')
  const filtered = search
    ? servizi.filter(s => s.nome.toLowerCase().includes(search.toLowerCase()))
    : servizi
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 0, height: '100%', minHeight: '50vh' }}>
      <div style={{ flexShrink: 0, position: 'relative', marginBottom: 8 }}>
        <Search size={14} style={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-3)' }} />
        <input
          type="text"
          placeholder="Cerca servizio…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ width: '100%', padding: '7px 8px 7px 28px', fontSize: 12, border: '1px solid var(--border)', borderRadius: 6, background: 'var(--surface-2, var(--background))', color: 'var(--text-1)', outline: 'none' }}
        />
      </div>
      <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', display: 'grid', gap: 2 }}>
        {filtered.map(s => (
          <div key={s.nome} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, padding: '6px 8px', borderRadius: 6, borderLeft: '3px solid #0ea5e9', background: 'var(--surface-2, transparent)' }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-1)', minWidth: 0, flex: 1 }}>{s.nome}</div>
            <span style={{ flexShrink: 0, padding: '2px 6px', fontSize: 9, fontWeight: 600, borderRadius: 8, background: '#0ea5e918', color: '#0ea5e9', whiteSpace: 'nowrap' }}>
              {s.count} / {numeroCdC} CdC
            </span>
          </div>
        ))}
        {filtered.length === 0 && (
          <div style={{ fontSize: 12, color: 'var(--text-3)', padding: 16, textAlign: 'center' }}>Nessun servizio trovato</div>
        )}
      </div>
    </div>
  )
}

export default function CdcSection({
  selectedAmbitoId,
  compareAmbiti,
  onSelectedAmbitoChange,
}: {
  selectedAmbitoId: AmbitoId | null
  compareAmbiti: boolean
  onSelectedAmbitoChange: (ambitoId: AmbitoId | null) => void
}) {
  const [selectedCdC, setSelectedCdC] = useState<string | null>(null)

  const handleSelectCdcFromMap = (nome: string) => {
    const selectedCdcEntry = CDC_WITH_AMBITO.find(entry => entry.casa.nome === nome)
    if (selectedCdcEntry) {
      onSelectedAmbitoChange(selectedCdcEntry.ambitoId)
    }
    setSelectedCdC(nome)
    setTimeout(() => {
      const el = document.querySelector('.telemetry-cdc-list-body')
      if (el) el.scrollTop = 0
    }, 50)
  }

  const cdcData = useMemo(() => {
    const filtered = selectedAmbitoId
      ? CDC_WITH_AMBITO.filter(c => c.ambitoId === selectedAmbitoId && c.casa.attiva === 1)
      : CDC_WITH_AMBITO.filter(c => c.casa.attiva === 1)

    const scopeAll = selectedAmbitoId
      ? CDC_WITH_AMBITO.filter(c => c.ambitoId === selectedAmbitoId)
      : CDC_WITH_AMBITO

    const numeroCdCAttive = scopeAll.filter(c => c.casa.attiva === 1).length
    const numeroCdCNonAttive = scopeAll.filter(c => c.casa.attiva !== 1).length
    const numeroCdCTotaliScope = scopeAll.length

    const numeroCdC = filtered.length
    const serviziUnici = getUniqueServices(filtered)
    const numeroServiziUnici = serviziUnici.length

    const cdcStatoData = [
      { name: 'Attive', value: numeroCdCAttive, color: '#16a34a' },
      { name: 'Non attive', value: numeroCdCNonAttive, color: '#dc2626' },
    ]
    const casePerElenco = [...scopeAll]
      .sort((a, b) => a.casa.nome.localeCompare(b.casa.nome, 'it'))

    const codiceSubmisuraCdc = 'I1.1 - Case della Comunità e presa in carico della persona'
    const interventiPnrrCdc = PNRR_INTERVENTI.filter(intervento => intervento.codiceTitoloSubmisura === codiceSubmisuraCdc)
    const interventiPnrrCdcFiltrati = selectedAmbitoId
      ? interventiPnrrCdc.filter(intervento => intervento.ambito === selectedAmbitoId)
      : interventiPnrrCdc
    const costoPnrrCdc = interventiPnrrCdcFiltrati.reduce((acc, intervento) => {
      const valore = Number(intervento.risorsePreviste.replace(/[^\d.-]/g, ''))
      return acc + (Number.isFinite(valore) ? valore : 0)
    }, 0)

    // Names of CdC for display
    const nomiCdC = filtered.map(c => c.casa.nome)

    // Statistiche servizi
    const totaleServizi = filtered.reduce((s, c) => s + c.casa.sezioni.reduce((s2, sz) => s2 + sz.servizi.length, 0), 0)
    const mediaServiziPerCdc = numeroCdC > 0 ? totaleServizi / numeroCdC : 0

    // Sezioni tematiche: quante CdC hanno ciascuna macro-area
    const sezioniMap = new Map<string, number>()
    for (const { casa } of filtered) {
      for (const sezione of casa.sezioni) {
        sezioniMap.set(sezione.titolo, (sezioniMap.get(sezione.titolo) ?? 0) + 1)
      }
    }
    const sezioneTematiche = Array.from(sezioniMap.entries())
      .map(([titolo, count]) => ({ titolo, count }))
      .sort((a, b) => b.count - a.count)

    // Servizi più diffusi: in quante CdC compare ciascun servizio
    const serviziDiffusiMap = new Map<string, number>()
    for (const { casa } of filtered) {
      const nomiVisti = new Set<string>()
      for (const sezione of casa.sezioni) {
        for (const servizio of sezione.servizi) {
          const nome = servizio.nome.trim()
          if (!nomiVisti.has(nome)) {
            nomiVisti.add(nome)
            serviziDiffusiMap.set(nome, (serviziDiffusiMap.get(nome) ?? 0) + 1)
          }
        }
      }
    }
    const serviziPiuDiffusi = Array.from(serviziDiffusiMap.entries())
      .map(([nome, count]) => ({ nome, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 8)

    const serviziTutti = Array.from(serviziDiffusiMap.entries())
      .map(([nome, count]) => ({ nome, count }))
      .sort((a, b) => b.count - a.count)

    const investimentoMedioPerCdc = numeroCdCAttive > 0 ? costoPnrrCdc / numeroCdCAttive : 0

    return {
      numeroCdC,
      numeroServiziUnici,
      costoPnrrCdc,
      numeroInterventiPnrrCdc: interventiPnrrCdcFiltrati.length,
      nomiCdC,
      cdcStatoData,
      numeroCdCAttive,
      numeroCdCNonAttive,
      numeroCdCTotaliScope,
      casePerElenco,
      totaleServizi,
      mediaServiziPerCdc,
      sezioneTematiche,
      serviziPiuDiffusi,
      serviziTutti,
      investimentoMedioPerCdc,
    }
  }, [selectedAmbitoId])

  const cdcCompareTotaleData = useMemo(() => {
    return AMBITI_STATS.map(ambito => ({
      id: ambito.id,
      name: ambito.nome,
      color: AMBITO_COMPARE_COLORS[ambito.id],
      value: CDC_WITH_AMBITO.filter(c => c.ambitoId === ambito.id).length,
    }))
  }, [])

  const cdcCompareAmbitiData = useMemo(() => {
    return AMBITI_STATS.map(ambito => ({
      id: ambito.id,
      name: ambito.nome,
      color: AMBITO_COMPARE_COLORS[ambito.id],
      value: CDC_WITH_AMBITO.filter(c => c.ambitoId === ambito.id && c.casa.attiva === 1).length,
    }))
  }, [])

  const cdcCompareServiziUniciData = useMemo(() => {
    return AMBITI_STATS.map(ambito => {
      const activeByAmbito = CDC_WITH_AMBITO.filter(c => c.ambitoId === ambito.id && c.casa.attiva === 1)
      return {
        id: ambito.id,
        name: ambito.nome,
        color: AMBITO_COMPARE_COLORS[ambito.id],
        value: getUniqueServices(activeByAmbito).length,
      }
    })
  }, [])

  const cdcCompareInvestimentiData = useMemo(() => {
    const codiceSubmisuraCdc = 'I1.1 - Case della Comunità e presa in carico della persona'
    const interventiPnrrCdc = PNRR_INTERVENTI.filter(intervento => intervento.codiceTitoloSubmisura === codiceSubmisuraCdc)

    return AMBITI_STATS.map(ambito => ({
      id: ambito.id,
      name: ambito.nome,
      color: AMBITO_COMPARE_COLORS[ambito.id],
      value: interventiPnrrCdc
        .filter(intervento => intervento.ambito === ambito.id)
        .reduce((acc, intervento) => {
          const valore = Number(intervento.risorsePreviste.replace(/[^\d.-]/g, ''))
          return acc + (Number.isFinite(valore) ? valore : 0)
        }, 0),
    }))
  }, [])

  const cdcPnrrPerCasaData = useMemo(() => {
    return [...cdcData.casePerElenco]
      .map(entry => {
        const nomePulito = entry.casa.nome
          .replace(/^Casa della Comunità di\s+/i, '')
          .replace(/^Casa della Comunita di\s+/i, '')
          .replace(/^Casa della Comunità\s+/i, '')
          .replace(/^Casa della Comunita\s+/i, '')
          .trim()
        return {
          nome: `CdC ${nomePulito}`,
          investimento: entry.pnrrInvestimento,
        }
      })
      .sort((a, b) => b.investimento - a.investimento)
  }, [cdcData.casePerElenco])

  const pnrrChartHeight = useMemo(() => {
    return cdcPnrrPerCasaData.reduce((acc, item) => {
      const lines = wrapCdcPnrrLabel(item.nome).length
      return acc + Math.max(34, lines * 12 + 14)
    }, 0)
  }, [cdcPnrrPerCasaData])

  const cdcServiziPerCasaData = useMemo(() => {
    return [...cdcData.casePerElenco]
      .map(entry => {
        const nomePulito = entry.casa.nome
          .replace(/^Casa della Comunità di\s+/i, '')
          .replace(/^Casa della Comunita di\s+/i, '')
          .replace(/^Casa della Comunità\s+/i, '')
          .replace(/^Casa della Comunita\s+/i, '')
          .trim()
        const serviziUnici = new Set<string>()
        for (const sezione of entry.casa.sezioni) {
          for (const servizio of sezione.servizi) {
            serviziUnici.add(servizio.nome.trim())
          }
        }
        return { nome: `CdC ${nomePulito}`, servizi: serviziUnici.size }
      })
      .sort((a, b) => b.servizi - a.servizi)
  }, [cdcData.casePerElenco])

  const cdcServiziChartHeight = useMemo(() => {
    return cdcServiziPerCasaData.reduce((acc, item) => {
      const lines = wrapCdcPnrrLabel(item.nome).length
      return acc + Math.max(34, lines * 12 + 14)
    }, 0)
  }, [cdcServiziPerCasaData])

  return (
    <>
      <h3 className="telemetry-section-title" style={{ marginBottom: 8 }}>Case delle Comunità</h3>

      <AutoGrid style={{ marginBottom: 14 }}>
        

        <BSDCard className="telemetry-map-card telemetry-cdc-map-card" kind="panel" sezione="Casa della Comunità" style={{ minHeight: 120, padding: 10 }} zoomable>
          <h3 className="telemetry-section-title" style={{ marginTop: 0, marginBottom: 8 }}>Seleziona l'ambito</h3>
          <DistrictMap
            onSelectDistrict={(id) => {
              onSelectedAmbitoChange(id as AmbitoId)
              setSelectedCdC(null)
            }}
            onClearSelection={() => {
              onSelectedAmbitoChange(null)
              setSelectedCdC(null)
            }}
            selectedDistrictId={selectedAmbitoId ?? null}
            showCdcMarkers
            showComuneCountInLegend={false}
            showLegend={false}
            showInstructionText={false}
            onSelectCdc={handleSelectCdcFromMap}
            selectedCdcNome={selectedCdC}
          />
        </BSDCard>

        <BSDCard
          kind="kpi"
          sezione="Casa della Comunità"
          label="Casa della Comunità"
          value={String(cdcData.casePerElenco.length)}
          icon={Building2}
          color="#0ea5e9"
          zoomable
          hideZoomButton
          sub={
            <p style={{ fontSize: 12, color: 'var(--text-2)', lineHeight: 1.6, margin: 0 }}>
              <strong>Le Case della Comunità (CdC)</strong> sono strutture del Servizio Sanitario Nazionale che offrono assistenza sanitaria e socio-sanitaria di prossimità. Rappresentano il punto di riferimento per l'accesso alle cure primarie.
            </p>
          }
          zoomSub={
            <CdcDialogList casePerElenco={cdcData.casePerElenco} />
          }
          footer={(openZoom) => (
            <button
              type="button"
              onClick={openZoom}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                width: '100%',
                padding: '6px 0',
                fontSize: 12,
                fontWeight: 600,
                color: 'var(--accent)',
                background: 'none',
                border: 'none',
                borderTop: '1px solid var(--border)',
                cursor: 'pointer',
                justifyContent: 'center',
                marginTop: 8,
                paddingTop: 8,
              }}
            >
              <List size={14} />
              Vedi elenco completo
            </button>
          )}
        />

        {compareAmbiti ? (
          <CompareAmbitiBarCard
            title="Case della Comunità"
            data={cdcCompareAmbitiData}
            formatter={formatInt}
            valueLabel="Numero CdC"
            sezione="Casa della Comunità"
          />
        ) : (
          <BSDCard
            kind="kpi"
            sezione="Casa della Comunità"
            label="Attive"
            value={`${cdcData.numeroCdCAttive} / ${cdcData.numeroCdCTotaliScope}`}
            icon={Heart}
            color="#e11d48"
            sub={selectedAmbitoId
              ? `Nell'ambito di ${AMBITI_STATS.find(a => a.id === selectedAmbitoId)?.nome ?? ''} sono previste ${cdcData.numeroCdCTotaliScope} Case della Comunità. Al momento sono attive ${cdcData.numeroCdCAttive} Case della Comunità.`
              : `Nella provincia di Monnza e della Brianza sono previste ${cdcData.numeroCdCTotaliScope} Case della Comunità. Al momento sono attive ${cdcData.numeroCdCAttive} Case della Comunità.`}
          />
        )}

        {compareAmbiti ? (
          <CompareAmbitiBarCard
            title="Servizi"
            data={cdcCompareServiziUniciData}
            formatter={formatInt}
            valueLabel="Numero servizi"
            sezione="Casa della Comunità"
          />
        ) : (
          <BSDCard
            kind="kpi"
            sezione="Casa della Comunità"
            label="Servizi"
            value={formatInt(cdcData.numeroServiziUnici)}
            icon={Building2}
            color="#0ea5e9"
            zoomable
            hideZoomButton
            sub="Numero di tipologie di servizi presenti nelle Case della Comunità"
            zoomSub={<ServiziDialogList servizi={cdcData.serviziTutti} numeroCdC={cdcData.numeroCdC} />}
            footer={(openZoom) => (
              <button
                type="button"
                onClick={openZoom}
                style={{ display: 'flex', alignItems: 'center', gap: 6, width: '100%', padding: '6px 0', fontSize: 12, fontWeight: 600, color: '#6366f1', background: 'none', border: 'none', borderTop: '1px solid var(--border)', cursor: 'pointer', justifyContent: 'center', marginTop: 8, paddingTop: 8 }}
              >
                <List size={14} />
                Vedi elenco completo
              </button>
            )}
          />
        )}

        {compareAmbiti ? (
          <CompareAmbitiBarCard
            title="Investimenti PNRR"
            data={cdcCompareInvestimentiData}
            formatter={formatEuro}
            valueLabel="Euro"
            sezione="Casa della Comunità"
          />
        ) : (
          <BSDCard
            kind="kpi"
            sezione="Casa della Comunità"
            label="Investimenti PNRR"
            value={formatEuro(cdcData.costoPnrrCdc)}
            icon={Banknote}
            color="#0284c7"
            sub={selectedAmbitoId
              ? `Totale nell'ambito ${AMBITI_STATS.find(a => a.id === selectedAmbitoId)?.nome ?? ''} · ${formatInt(cdcData.numeroInterventiPnrrCdc)} Case della Comunità`
              : `Totale su tutti gli ambiti · ${formatInt(cdcData.numeroInterventiPnrrCdc)} Case della Comunità`}
          />
        )}

        
      </AutoGrid>

      {cdcData.casePerElenco.length > 0 && (
        <>
          <h3 className="telemetry-section-title" style={{ marginTop: 16, marginBottom: 8 }}>
            Comparazione tra case della comunità
          </h3>
          <AutoGrid>
            <BSDCard
              kind="panel"
              zoomable
              sezione="Casa della Comunità"
              style={{ display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden' }}
            >
              <h3 className="telemetry-section-title" style={{ marginTop: 0, marginBottom: 4 }}>Servizi per casa della comunità</h3>
              <div style={{ flex: 1, minHeight: 0, maxHeight: 200, overflowY: 'auto', overflowX: 'hidden' }}>
                <ResponsiveContainer width="100%" height={cdcServiziChartHeight}>
                  <BarChart data={cdcServiziPerCasaData} layout="vertical" margin={{ top: 2, right: 22, bottom: 2, left: 6 }}>
                    <XAxis type="number" hide allowDecimals={false} />
                    <YAxis type="category" dataKey="nome" tick={<CdcPnrrAxisTick />} axisLine={false} tickLine={false} width={CDC_PNRR_AXIS_WIDTH} />
                    <Tooltip
                      contentStyle={{ background: 'var(--tooltip-bg)', border: '1px solid var(--tooltip-border)', borderRadius: 8, fontSize: 12, color: 'var(--tooltip-text)' }}
                      formatter={(v: number | string | undefined) => [formatInt(Number(v ?? 0)), 'Servizi']}
                    />
                    <Bar dataKey="servizi" fill="#0ea5e9" radius={[0, 6, 6, 0]} barSize={11}>
                      <LabelList dataKey="servizi" position="right" formatter={(v: unknown) => formatInt(Number(v ?? 0))} fill="var(--text-2)" fontSize={10} />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </BSDCard>

            <BSDCard
              kind="panel"
              zoomable
              sezione="Casa della Comunità"
              style={{ display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden' }}
            >
              <h3 className="telemetry-section-title" style={{ marginTop: 0, marginBottom: 4 }}>Investimenti PNRR per casa della comunità</h3>
              <div style={{ flex: 1, minHeight: 0, maxHeight: 200, overflowY: 'auto', overflowX: 'hidden' }}>
                {cdcPnrrPerCasaData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={pnrrChartHeight}>
                    <BarChart data={cdcPnrrPerCasaData} layout="vertical" margin={{ top: 2, right: 22, bottom: 2, left: 6 }}>
                      <XAxis type="number" hide allowDecimals={false} />
                      <YAxis type="category" dataKey="nome" tick={<CdcPnrrAxisTick />} axisLine={false} tickLine={false} width={CDC_PNRR_AXIS_WIDTH} />
                      <Tooltip content={<PnrrTooltip />} cursor={{ fill: 'rgba(255,255,255,0.04)' }} />
                      <Bar dataKey="investimento" fill="#0284c7" radius={[0, 6, 6, 0]} barSize={11}>
                        <LabelList dataKey="investimento" position="right" formatter={(v: unknown) => formatEuro(Number(v ?? 0))} fill="var(--text-2)" fontSize={10} />
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div style={{ fontSize: 12, color: 'var(--text-2)' }}>Nessun dato disponibile</div>
                )}
              </div>
            </BSDCard>
          </AutoGrid>
        </>
      )}

      { (
        <>
          <h3 className="telemetry-section-title" style={{ marginTop: 16, marginBottom: 8 }}>
            Comparazione tra ambiti
          </h3>
          <AutoGrid>
            <CompareAmbitiBarCard
              sezione="Casa della Comunità"
              title="Case della Comunità"
              data={cdcCompareTotaleData}
              formatter={formatInt}
              valueLabel="CdC"
            />
            <CompareAmbitiBarCard
              sezione="Casa della Comunità"
              title="Attive"
              data={cdcCompareAmbitiData}
              formatter={formatInt}
              valueLabel="CdC attive"
            />
            <CompareAmbitiBarCard
              sezione="Casa della Comunità"
              title="Servizi"
              data={cdcCompareServiziUniciData}
              formatter={formatInt}
              valueLabel="servizi unici"
            />
            <CompareAmbitiBarCard
              sezione="Casa della Comunità"
              title="Investimenti PNRR"
              data={cdcCompareInvestimentiData}
              formatter={formatEuro}
              valueLabel="Euro"
            />
          </AutoGrid>
        </>
      )}
    </>
  )
}
